import React from 'react';
import { useTranslation  } from 'react-i18next';
import { Divider, Popover, PopoverContent, PopoverTrigger } from "@heroui/react";
import { /*Routes, Route,*/ useNavigate, useParams } from 'react-router-dom';

import { store, useAppSelector } from '@/app/store';
import { padZeros, RoleContext } from '@/app/api/common';
import { LoginDescriptor } from '@/features/logins/loginsSlice';
import { checkNoError, getUrl, getField, languageSelectorAclKey, UrlInfo } from '@/features/channels/channelsSlice';
import { canOverrideStatistics } from '@/features/appusage/appUsageApi';

import { copyProjectInfoPublic, copyProjectLimits, getNewProject, getProjectById, HotelInfo, HotelInfoPublic, HotelLimits, HotelRoom } from '@/features/projects/projectsSlice';
import { findLightRow, mwLightOverlaps } from '@/features/projects/mwLight';

import { usePageSubscription } from '@/app/websocket/hooks';
import type { SubscriptionTarget } from '@/app/websocket/types';
import BreadCrumbs from '@/components/Common/BreadCrumbs';
import Button from '@/components/Common/Button';
import { FieldInfo } from '@/components/Common/Dropdown';
import FlexRow, { FlexRowProps } from '@/components/Common/FlexRow';
import MenuItem from '@/components/Common/MenuItem';
import Tabs from '@/components/Common/Tabs';
import UrlsEditor from '@/components/Common/UrlsEditor';

import EnabledApps  from './EnabledApps';
import ProjectLocalUsers from './ProjectLocalUsers';
import ProjectRooms from './ProjectRooms';
import ProjectRoom from './ProjectRoom';
import ProjectSettings from './ProjectSettings';
import ProjectStatistics from './ProjectStatistics';
import { validateLanguageSelector } from './LanguageSelectorSettings';
import ProjectTheme from './ProjectTheme';
import WelcomeWizard from './WelcomeWizard';

import ChannelLists from '@/pages/ChannelLists/ChannelLists';
import ChannelList from '@/pages/ChannelLists/ChannelList';
import Channels from '@/pages/Channels/Channels';
import Channel from '@/pages/Channels/Channel';

export interface NumberTextProps {
  children?:			React.ReactNode;
}

const NumberText = (props: NumberTextProps) => {
  return (
  <span className="text-[#1B69AA]">
    { props.children }
  </span>
  );
};

export interface ProjectRow {
  id:					string;
  name:					string;
  rooms_max:    string,
  rooms_active: string,
  rooms_live:   string,
  used_limit:			string;
  location:				string;
  bexio_url:			string;
  owner:				string;
  contact:				string;
}

const dummyFunc = () => { return; };

const Project = () => {
  const { t } = useTranslation();
  
  const { id, tab, tab_no, sub_tab } = useParams();
  const navigate = useNavigate();
  const projects = useAppSelector((state) => state.projects);
  const role = React.useContext(RoleContext);

  const [project, setProject] = React.useState(null as HotelInfo | null);
  const [pub, setPub] = React.useState(null as HotelInfoPublic | null);
  const [changes, setChanges] = React.useState(false);
  const [triedSave, SetTriedSave] = React.useState(false);
  const [urlInvalid, setUrlInvalid] = React.useState(false);
  // Set once GET /statistics.json answers 503 (not enabled server-wide) or a
  // flat 403 refusal — both mean "hide the whole tab", same contract as the
  // room-level screen time tab (ProjectRoom.tsx screenTimeHidden).
  const [statisticsHidden, setStatisticsHidden] = React.useState(false);

  const handleStatisticsAvailability = React.useCallback((available: boolean) => {
    setStatisticsHidden(!available);
  }, []);

  const saveList = React.useRef(dummyFunc);
  const saveChannel = React.useRef(dummyFunc);
  
  const active =	tab
  					?	tab
  					:	"";

  // Subscribe to WebSocket events relevant to the current tab only
  const subscriptionTargets = React.useMemo((): SubscriptionTarget[] => {
    if (!id || id === '__new__') return [];
    const base: SubscriptionTarget[] = [{ type: 'project', scope_id: id }];
    switch (active) {
      case '':
      case 'welcome_wizard':
      case 'manager_acls':
      case 'theme':
        return [...base, { type: 'project_settings', scope_id: id }];
      case 'rooms':
        return [...base, { type: 'room', scope_id: id }, { type: 'room_info', scope_id: id }];
      case 'channel_lists':
        return [...base, { type: 'local_channel_list', scope_id: id }];
      case 'apps':
        return [...base, { type: 'app' }];
      default:
        return base;
    }
  }, [id, active]);
  usePageSubscription(subscriptionTargets);

  const caption =	id === "__new__"
  					?	t('projects:project:new')
  					:	(	project
  							?	project.name
  							:	t('global:loading')
  						);
  
  const limits = 	pub
 	    			?	pub.limits
 	    			:	null;

  const logins = useAppSelector((state) => state.logins);
  const isProjectMember = React.useMemo(() => {
    const username = logins.login?.username;
    if (!username || !project) return false;

    const localProjectId = logins.login?.local_project_id;
    if (localProjectId && localProjectId === project.id) return true;

    if (project.owner === username) return true;
    if (!limits) return false;
    const execs = limits.executives;
    return execs?.partner?.includes(username) ||
           execs?.chain_manager?.includes(username) ||
           execs?.manager?.includes(username);
  }, [logins.login, project, limits]);

  const activePlatform = React.useMemo(() => {
    const filteredProject = projects.projects.find((data: HotelInfo) => data.id === id);
    return filteredProject?.stats?.rooms?.platforms ?? [];
  }, [projects.projects, id]);

  //console.log(`Project/id=${id}/tab=${tab}/tab_no=${tab_no}/sub_tab=${sub_tab}`);

  const isNew = id === '__new__';

  // §2.3 — a deep link to /projects/:id for a project the list marks as an MW
  // light row goes to /mw_light/:id instead. The redirect has to happen BEFORE
  // the full project read: that read answers 403 for an MW light user (§3.2),
  // which apiOperation would turn into a toast on a page they are leaving
  // anyway. The fetch effect below carries the same guard, because it keys on
  // `project` rather than on `id` and would otherwise win the race.
  const isLightProject = React.useMemo(
    () =>	!!id &&
			id !== '__new__' &&
			!!findLightRow(id, projects.projects),
    [id, projects.projects]
  );

  React.useEffect(() => {
    if (	!isLightProject ||
			!id
		 )
      return;

    navigate(`/mw_light/${encodeURIComponent(id)}`, { replace: true });
  }, [isLightProject, id, navigate]);

  // Its own `enable-statistics` Manager ACL — separate from `enable-screentime`,
  // which stays scoped to setting the screen time allowance/window/message.
  // While the new key is unset it falls back to the screen-time grant, so
  // deploy changes nothing until an admin explicitly toggles the new
  // "Statistics" checkbox in the Manager ACL editor (canOverrideStatistics).
  const statisticsEnabled = React.useMemo(() => {
    const aclsUrls = limits?.acls ?? [];
    const hasSeeAll = role?.privilages?.includes("SeeAllProjects");

    return role.admin || hasSeeAll || canOverrideStatistics(aclsUrls);
  }, [role.admin, role?.privilages, limits]);

  const statisticsAvailable = statisticsHidden ? false : statisticsEnabled;

  const tabs = React.useMemo(() => {
    if (isNew && !role.admin) {
      return [
        { value: "",               desc: "projects:project:project_settings:caption" },
      ] as FieldInfo[];
    }
    const aclsUrls = limits?.acls ?? [];
    const hasSeeAll = role?.privilages?.includes("SeeAllProjects");
    const hasAnyPriv = role?.admin || (role?.privilages && role.privilages.length > 0);
    const hasAccess = (aclKey: string) => role?.admin || getField(aclsUrls, aclKey) == "1";
    return [
      { value: "",               desc: "projects:project:project_settings:caption" },
      { value: "welcome_wizard", desc: "projects:project:welcome_wizard:caption" },
      ...(role.admin || role?.privilages?.includes("SetManagerACLs") || hasSeeAll ? [{ value: "manager_acls", desc: "projects:project:manager_acls:caption" }] : []),
      ...((hasAccess("enable-theme") || hasSeeAll) ? [{ value: "theme", desc: "projects:project:theme:caption" }] : []),
      ...((hasAccess("enable-apps") || hasSeeAll) ? [{ value: "apps", desc: "projects:project:apps:caption" }] : []),
      { value: "rooms",          desc: "projects:project:rooms:caption" },
      ...((hasAccess("enable-channellist") || hasSeeAll) ? [{ value: "channel_lists", desc: "projects:project:channel_lists:caption" }] : []),
      ...(statisticsAvailable ? [{ value: "statistics", desc: "projects:project:statistics:caption" }] : []),
      ...((role.admin || role?.privilages?.includes("ViewLocalUsers") || role?.privilages?.includes("ChangeLocalUsers")) ? [{ value: "local_users", desc: "projects:project:local_users:caption" }] : []),
    ] as FieldInfo[];
  }, [role.admin, role?.privilages, limits, isNew, statisticsAvailable]);
  
  React.useEffect(() => {
    //console.log("Project/id=" + id + "/projects=", projects, "/project=", project);
    if (!id)
      return;

    if (	id === '__new__' &&
    		project &&
    		project.id == ''
       )
      return;

    setProject(	id === '__new__'
    			?	getNewProject()
    			:	getProjectById(id, projects.projects)
    		  );
  }, [id, projects.projects]);

  // Reset changes only when navigating to a different project
  React.useEffect(() => {
	setChanges(false);
  }, [id]);
  
  /*React.useEffect(() => {
    if (!tab)
      return;
    
    if (	tab &&
    		tab == 'rooms' &&
    		tab_no
       ) {
      setTabs(roomTabs);
    } else {
      setTabs(projectTabs);
    }
  }, [tab]);*/
  
  React.useEffect(() => {
    if (	project &&
    		pub &&
    		project.id == pub.id
       )
      return;

    setPub(null);

    if (	project &&
    		project.is_new
       )
      return;

    // §2.3 — never read an MW light project here; the redirect above is on its
    // way and this read would only earn a 403 toast.
    if (isLightProject)
      return;

    store.getProjects(
    	project &&
    	project.id
    	?	project.id
    	:	undefined
    );
  }, [project, isLightProject]);

  // §3.2 — the System settings Checkout switch greys the Netflix box and gives
  // the room Checkout panel its first word (the panel then follows its own
  // /checkout.json answers). Only Settings pages fetched /master.json so far,
  // so fetch it here too. Admins only: anyone else gets a 403 (and a toast)
  // from it, and sees neither piece.
  React.useEffect(() => {
    if (!role.admin) return;

    store.getMaster();
  }, [role.admin]);

  // Call firmware API when navigating into a project (requires CreateFirmware privilege)
  React.useEffect(() => {
    if (!project || project.is_new || !project.id) return;
    if (!role.admin && !role?.privilages?.includes("ChangeFirmware")) return;

    console.log("Project/useEffect/calling getProjectFirmware for id=" + project.id);
    store.getProjectFirmware(project.id);
  }, [project?.id, role.admin, role?.privilages]);
  
  React.useEffect(() => {
    const active = projects.active ? copyProjectInfoPublic(projects.active) : null;

    if (active && active.limits && active.limits.channel_lists)
      active.limits.channel_lists.sort((a, b) => a.start_from - b.start_from);

    console.log("Project/useEffect/active=", active);
    setPub(active);

    if (active && active.languages && project) {
      const proj = { ...project, languages: active.languages };
      setProject(proj);
    }

  }, [projects.active]);
  
  const idInvalid = React.useMemo(() => {
    return !project || project.id.trim() =='';
  }, [project]);
  
  const nameInvalid = React.useMemo(() => {
    return !project || project.name.trim() =='';
  }, [project]);

  // §2.1 — a name in MW light AND in Partner / Chain manager / Manager is only
  // possible in old data, and the server refuses an admin save that still has
  // one. Block it here too, so the refusal is a warning next to the lists
  // instead of a toast after a lost round trip. ProjectSettings renders the
  // per-name message unconditionally, so there is nothing to reveal on
  // triedSave.
  const mwLightOverlapInvalid = React.useMemo(() => {
    if (	!role.admin ||
			!limits
		 )
      return false;

    return mwLightOverlaps(limits.executives).length > 0;
  }, [role.admin, limits]);
  

  const firmwareInvalid = React.useMemo(() => {
    const firmware = limits?.overrides?.firmware;
    if (!firmware || firmware.length === 0) return false;

    // Check each firmware entry
    for (const fw of firmware) {
      // If platform is selected, model must be selected
      if (fw.platform && fw.platform !== '' && (!fw.model || fw.model === '')) {
        return true;
      }
      // If model is selected, version must be selected
      if (fw.model && fw.model !== '' && (!fw.version || fw.version === '')) {
        return true;
      }
    }
    return false;
  }, [limits]);

  const hotlineInvalid = React.useMemo(() => {
    const hotline = limits && limits.welcome && limits.welcome.fields
      ? getField(limits.welcome.fields, "hotline")
      : "";
    return hotline.length > 20;
  }, [limits]);

  // Ticking Enabled before adding any choices is the easiest way to trip
  // this — see LanguageSelectorSettings.tsx / §4.5. Same shape as the other
  // *Invalid checks: it blocks the WHOLE project save, not just this panel,
  // because the panel that explains why is closed by the time Save is tried.
  const languageSelectorInvalid = React.useMemo(() => {
    return !validateLanguageSelector(limits?.language_selector).valid;
  }, [limits]);

  /*const partnerInvalid = React.useMemo(() => {
    return	limits &&
    		(	!limits.executives.partner ||
    			limits.executives.partner.length == 0
    		)
    		?	true
    		:	false;
  }, [limits]);
  
  const chainManagerInvalid = React.useMemo(() => {
    return	limits &&
    		(	!limits.executives.chain_manager ||
    			limits.executives.chain_manager.length == 0
    		)
    		?	true
    		:	false;
  }, [limits]);
  
  const managerInvalid = React.useMemo(() => {
    return	limits &&
    		(	!limits.executives.manager ||
    			limits.executives.manager.length == 0
    		)
    		?	true
    		:	false;
  }, [limits]);*/
  
  const updateActive = (act: any) => {
    const url = `/projects/${id}/${act}`;
    navigate(url);
  
    console.log("Project/Tabs/updateActive/act=" + act + "/url=" + url);
  };

  // If the tab is retired while we are standing on it (ACL revoked, or the
  // 503/403 availability probe comes back after the click that opened it),
  // walk back to Info — otherwise the tab vanishes under a blank panel.
  React.useEffect(() => {
    if (	active == 'statistics' &&
			limits &&
			!statisticsAvailable
		 )
      updateActive('');
  }, [active, limits, statisticsAvailable]);

  const updateProject = (proj: HotelInfo) => {
    setChanges(true);
    setProject(proj);
  };
  
  const updatePub = React.useCallback((pub: HotelInfoPublic) => {
    setChanges(true);
    setPub(pub);
  }, []);
  
  const updateLimits = React.useCallback((lim: HotelLimits) => {
    if (!pub)
      return;
    
    let newPub = copyProjectInfoPublic(pub);
    newPub.limits = lim;
    
    updatePub(newPub);
    
    console.log("Project/updateLimits/newPub=", newPub);
  }, [pub, updatePub]);
  
  const updateRoom = React.useCallback(async (room: HotelRoom): Promise<boolean> => {
    const mess = await store.setRoom(room);
    console.log("Project/handleSave/mess=", mess);
    
    if (!checkNoError(mess))
      return false;

    if (project) {
      store.getProjects(project.id);
      return true;
    }
    
    return false;
  }, [project]);
  
  const handleCallback = React.useCallback((cmd: string, callback: () => any) => {
    console.log("Project/handleCallback/cmd=" + cmd, "/callback=", callback);
    switch(cmd) {
    case "save_list":
      saveList.current = callback;
      break;
    case "save_channel":
      saveChannel.current = callback;
      break;
    }
  }, []);

  const updateWelcomeFields = React.useCallback((urls: UrlInfo[]) => {
    if (!limits)
      return;
    
    if (urlInvalid) {
      SetTriedSave(true);
      return;
    }
    
    let limit = copyProjectLimits(limits);
    limit.welcome.fields = urls;
    
    updateLimits(limit);
  }, [limits, SetTriedSave, urlInvalid, updateRoom]);
  
  
  const handleSave = async () => {
    if (!project)
      return;

    const canEditProject = role.admin || role?.privilages?.includes("EditProjectSettings") || (project.is_new && role?.privilages?.includes("CreateProjects"));

    const hasLanguageAcl = limits?.acls && getField(limits.acls, "enable-language") == "1";
    const hasLanguageSelectorAcl = limits?.acls && getField(limits.acls, languageSelectorAclKey) == "1";
    if (!canEditProject && !role?.privilages?.includes("SetPartner") && !role?.privilages?.includes("SetChainmanager") && !role?.privilages?.includes("SetManager") && !role?.privilages?.includes("SetManagerACLs") && !role?.privilages?.includes("SetChannelList") && !isProjectMember && !hasLanguageAcl && !hasLanguageSelectorAcl)
      return;

    if (	(canEditProject && nameInvalid) ||
    		//partnerInvalid ||
    		//managerInvalid ||
    		(canEditProject && urlInvalid) ||
    		(canEditProject && firmwareInvalid) ||
    		(canEditProject && hotlineInvalid) ||
    		mwLightOverlapInvalid ||
    		languageSelectorInvalid
       ) {
      SetTriedSave(true);
      return;
    }

    switch(active) {
    case 'channel_lists':
      saveList.current();
      return;
      break;
    case 'channels':
      console.log("Project/handleSave/active=" + active + "/saveChannel=", saveChannel);
      saveChannel.current();
      return;
      break;
    }

    if (canEditProject || hasLanguageAcl || isProjectMember) {
      const mess = await store.setProject(project);
      const wasNew = project && project.is_new;

      console.log("Project/handleSave/mess=", mess);

      if (!checkNoError(mess))
        return;

      if (	mess &&
              wasNew
         ) {
        navigate(`/projects/${mess.errors[0].result.id}/${active}`);
        return;
      }
    }

    if (	!pub ||
        	!pub.limits
       )
      return;

    const limitsToSave = copyProjectLimits(pub.limits);
    limitsToSave.channel_lists.forEach((desc, i) => { desc.start_from = i + 1; });

    const mess2 = await store.setLimits(limitsToSave);
    
    if (!checkNoError(mess2))
      return;
    
    SetTriedSave(false);
    setChanges(false);

    setTimeout(() => store.getProjects(project.id), 500);
    store.getChannelLists(project.id);

    // #91 §0.8 — a hotel Checkout change moves the hotel in or out of
    // System settings' checkout_deviations.
    if (role.admin)
      store.getMaster();
  };
  
  if (!project)
    return null;

  if (	tab == 'rooms' &&
     	tab_no &&
     	(	!sub_tab ||
     		sub_tab != 'clone'
     	)
     ) {
    return (
    <ProjectRoom project={project}
                 setProject={updateProject}
                 room_no={tab_no ? tab_no : ''}
                 pub={pub}
                 limits={limits}
                 setLimits={updateLimits}
                 setRoom={updateRoom}
                 triedSave={triedSave}
                 setUrlInvalid={setUrlInvalid} />
    );
  }
  
  const crumbs = [
    [	`/projects/${id}`,
     	caption,
    ],
  ];
  
  //const cannotSave = (triedSave && (nameInvalid || ownerInvalid || partnerInvalid || managerInvalid || urlInvalid));
  const cannotSave = triedSave && (nameInvalid || urlInvalid || firmwareInvalid || hotlineInvalid || languageSelectorInvalid);

  console.log("Project/render/changes=" + changes + "/cannotSave=" + cannotSave + "/hotlineInvalid=" + hotlineInvalid + "/triedSave=" + triedSave + "/firmwareInvalid=" + firmwareInvalid + "/isDisabled=" + (!changes || cannotSave));
  
  const welcomeFields =	limits &&
  						limits.welcome &&
  						limits.welcome.fields
				    	?	limits.welcome.fields
				    	:	[];
  
  /*if (	active &&
      	pub
     ) {
    crumbs.push([
      `/projects/${pub.id}/${active}`,
      t(`projects:project:${active}:caption`),
    ]);
  }*/
  
  return (
  <React.Fragment>
	<FlexRow direction="column"
	         align="start"
	         className="pt-[32px] px-[32px] bg-white sticky top-[64px] z-50">
	  <BreadCrumbs pages={crumbs} />
	{project && project.stats &&
	  <FlexRow className="gap-[5px]">
	    <span className="material-icons !text-[--Primetime-main] text-[16px]">
	      location_on
	    </span>
	    <span className="!text-black text-[14px]">
	      { t("projects:total") }
	    </span>
	    <span className="text-[--Primetime-main] text-[14px]">
	      { padZeros(project.stats.rooms.allocated, 3) }
	    </span>
	    <span className="!text-black text-[14px]">
	      { t("projects:activated") }
	    </span>
	    <span className="text-[--Primetime-main] text-[14px]">
	      { padZeros(project.stats.rooms.created, 3) }
	    </span>
	    <span className="!text-black text-[14px]">
	      { t("projects:active") }
	    </span>
	    <span className="text-[--Primetime-main] text-[14px]">
	      { padZeros(project.stats.rooms.connected, 3) }
	    </span>
	  </FlexRow>
	}
      <FlexRow className="py-[20px]">
        <Tabs tabs={tabs}
              flex={1}
              active={active}
              setActive={updateActive} />
      </FlexRow>
	</FlexRow>
    <FlexRow direction="column"
             align="start"
             className="px-[32px]">
    {project &&
      <React.Fragment>
      {active == '' &&
        <ProjectSettings project={project}
                         setProject={updateProject}
                         limits={limits}
                         setLimits={updateLimits}
                         triedSave={triedSave}
                         idInvalid={idInvalid}
                         nameInvalid={nameInvalid}
                         setUrlInvalid={setUrlInvalid}  />
      }
      {active == 'welcome_wizard' &&
        <React.Fragment>
          <WelcomeWizard project={project}
                         setProject={updateProject}
                         limits={limits}
                         setLimits={updateLimits} />
        {role.admin &&
          <FlexRow direction="column"
           align="start"
           className="py-0 gap-[16px] max-w-[448px]">
            <CatHeader caption="common:urls:caption"
	                   margin="mt-[8px]" />
            <UrlsEditor urls={welcomeFields}
				        setUrls={updateWelcomeFields}
				        triedSave={triedSave}
				        setUrlInvalid={setUrlInvalid} />
		  </FlexRow>
		}
		</React.Fragment>
      }
      {active == 'manager_acls' &&
        <WelcomeWizard project={project}
                       setProject={updateProject}
                       acls={true}
                       limits={limits}
                       setLimits={updateLimits} />
      }
      {active == 'theme' &&
        <ProjectTheme project={project}
                      setProject={updateProject}
                      limits={limits}
                      setLimits={updateLimits} />
      }
      {active == 'rooms' &&
        <ProjectRooms project={project}
                      setProject={updateProject}
                      pub={pub}
                      limits={limits}
                      setLimits={updateLimits}
                      setRoom={updateRoom} />
      }
      {	active == 'channel_lists' &&
        pub &&
        (	sub_tab
    		?	<ChannelList project={pub}
    		                 setlocalChannel={true}
                      		 registerCallback={handleCallback}
                      		 setChanges={setChanges} />
            :	<ChannelLists project={pub}
                            setlocalChannel={true}
                      		  registerCallback={handleCallback}
                      		  setChanges={setChanges} />
        )
      }
      {	active == 'channels' &&
        pub &&
        (	tab_no
    		?	<Channel project={pub}
                      	 registerCallback={handleCallback}
                      	 setChanges={setChanges} />
            :	<Channels project={pub}
                      	  registerCallback={handleCallback}
                      	  setChanges={setChanges} />
        )
      }
      {	active == 'apps' &&
        <EnabledApps project={project}
                     setProject={updateProject}
                     limits={limits}
                     setLimits={updateLimits} />
      }
      {	active == 'local_users' &&
        <ProjectLocalUsers project={project} />
      }
      {	active == 'statistics' &&
        statisticsAvailable &&
        <ProjectStatistics project={project}
                           pub={pub}
                           onAvailability={handleStatisticsAvailability} />
      }
      </React.Fragment>
    }
    </FlexRow>
    {active != 'channel_lists' && active != 'statistics' && (role.admin || role?.privilages?.includes("EditProjectSettings") || (isNew && role?.privilages?.includes("CreateProjects")) || ((active == '' || active == 'welcome_wizard' || active == 'rooms' || active == 'apps' || active == 'theme') && (role?.privilages?.includes("SetPartner") || role?.privilages?.includes("SetChainmanager") || role?.privilages?.includes("SetManager"))) || ((active == 'welcome_wizard' || active == 'apps' || active == 'theme') && isProjectMember) || (active == '' && (role?.privilages?.includes("SetChannelList") || (limits?.acls && getField(limits.acls, "enable-language") == "1") || (limits?.acls && getField(limits.acls, languageSelectorAclKey) == "1"))) || (active == 'manager_acls' && role?.privilages?.includes("SetManagerACLs"))) &&
    <FlexRow className="sticky bottom-0 p-[32px] bg-white z-10">
        <Button caption={t('projects:project:save')}
                className="uppercase"
                icon="save"
                isDisabled={!changes || cannotSave}
                //className="mt-[132px]"
                onClick={() => handleSave()} />
    </FlexRow>
    }
  </React.Fragment>
  );
};

export interface CatHeaderProps extends FlexRowProps {
  caption:				string;
  margin?:				string;
  captionProps?:		any;
}

export const CatHeader = (cprops: CatHeaderProps) => {
  const { t } = useTranslation();
  
  const {caption, captionProps, className, margin, ...props} = cprops;
  
  const cn = [
    margin ? margin : 'mt-[32px]',
    "pb-[8px] b14 text-black",
    ...(Array.isArray(className) ? className : [className]),
  ];
  
  const label =	(t(caption, captionProps) as unknown) as string;
  
  return (
  <FlexRow className={cn}
           {...props}>
    <span>
      { label }
    </span>
  </FlexRow>
  );
};

export default Project;
